import React, { Component } from 'react';
import StoryblokReact, { SbEditableContent } from 'storyblok-react';
import StoryblokClient, { ISbStory } from 'storyblok-js-client';
import { getComponent } from '../components';
import { DomService, StoryblokService, NavigationService, StoryblokDatasourceEntry } from '../services';
import { EntryData, StoryDataFromGraphQLQuery } from '../templates/template.types';
import { RcmCountrySwitchModal } from '../components/custom/country-switch-modal';
import { RcmUserSwitchModal } from '../components/custom/user-switch-modal';
import { GoogleTagManager } from '../components/custom/google-tag-manager';
import { Jentis } from '../components/custom/jentis';
import { RcmIEModal } from '../components/custom/ie-modal';

type StoryblokEntryState = EntryData & { showIEModal: boolean };

const RcmGlobalConfig = getComponent('rcm-global-config') as React.ElementType;
const RcmGlobalContent = getComponent('rcm-global-content') as React.ElementType;
const Navigation = getComponent('rcm-navigation') as React.ElementType;
const Footer = getComponent('rcm-footer') as React.ElementType;
const Container = 'rcm-layout-container' as React.ElementType;
const RcmNavigationSalzburg = 'rcm-navigation-salzburg' as React.ElementType;
const RcmNavigationGolfmeisterschaft = 'rcm-navigation-golfmeisterschaft' as React.ElementType;

const FundsDetail = 'rcm-layout-fund' as React.ElementType;
const ContactButton = 'rcm-contact-button' as React.ElementType;

// eslint-disable-next-line import/no-default-export
export default class StoryblokEntry extends Component<Record<string, unknown>, StoryblokEntryState> {
  private storyblokClient: StoryblokClient;

  public constructor(props: Record<string, unknown>) {
    super(props);
    this.handleStoryblokLoad = this.handleStoryblokLoad.bind(this);
    this.loadStory = this.loadStory.bind(this);

    this.state = {} as StoryblokEntryState;
  }

  public componentDidMount(): void {
    this.storyblokClient = new StoryblokClient({
      accessToken: StoryblokService.getConfig().options.accessToken as string,
    });
    StoryblokService.loadStoryblokBridge(this.handleStoryblokLoad);
  }

  public render(): JSX.Element {
    const { story, navigation, globalContent, showIEModal, ...globalConfig } = this.state;

    if (story?.content?.component === 'page') {
      const nestableArticles = story.content.body?.find((item: SbEditableContent) => item.component === 'articles');
      if (nestableArticles) {
        nestableArticles.component = 'rcm-layout-articles';
      }
    }

    if (story) {
      return (
        <StoryblokReact content={story.content}>
          {/* TODO: Remove GTM from editor view after tracking was tested by Oli */}
          <RcmGlobalConfig {...globalConfig} />
          <RcmGlobalContent globalContent={globalContent} />
          <GoogleTagManager googleTagManagerId="GTM-KKV7T5J" />
          <Jentis globalConfig={globalConfig} />
          <RcmCountrySwitchModal globalContent={globalContent} alwaysOpen={StoryblokService.getCountryCode(story).countryCode === 'rcm-int'} />
          <RcmUserSwitchModal />
          <RcmIEModal globalContent={globalContent} show={showIEModal} />
          {globalConfig.locale === 'rsi' && <RcmNavigationSalzburg />}

          {globalConfig.locale === 'golfmeisterschaft' && <RcmNavigationGolfmeisterschaft />}

          {!['rsi', 'golfmeisterschaft'].includes(globalConfig.locale) && (
            <Navigation
              tree={navigation}
              getComponent={getComponent}
              userTypeFromSlug={StoryblokService.getUserTypeFromSlug(story)}
              countryCode={StoryblokService.getCountryCode(story).countryCode}
              currentCountry={StoryblokService.getCountryCode(story).country}
              currentLanguage={StoryblokService.getCountryCode(story).locale}
              alternates={JSON.stringify(story.alternates)}
            />
          )}
          <Container kind={globalConfig.locale === 'rsi' || globalConfig.locale === 'golfmeisterschaft' ? 'full' : 'normal'}>
            <FundsDetail slot="content" />
          </Container>
          {StoryblokService.getCountryCode(story).countryCode !== 'not-found' &&
            StoryblokService.getCountryCode(story).countryCode !== 'rcm-int' &&
            globalContent.contact.button.isEnabled === '1' && (
              <ContactButton link={globalContent?.contact?.button?.link} name={globalContent?.contact?.button?.name} />
            )}
          <Footer
            isSalzburg={globalConfig.locale === 'rsi'}
            tree={navigation}
            getComponent={getComponent}
            userTypeFromSlug={StoryblokService.getUserTypeFromSlug(story)}
            countryCode={StoryblokService.getCountryCode(story).countryCode}
          />
          {/* End Google Tag Manager (noscript) */}
          {/* TODO: Remove GTM from editor view after tracking was tested by Oli */}
          <noscript>
            <iframe
              src={`https://www.googletagmanager.com/ns.html?id=${globalContent?.gtmId}`}
              height="0"
              width="0"
              style={{ display: 'none', visibility: 'hidden' }}
            />
          </noscript>
          {/* End Google Tag Manager (noscript) */}
        </StoryblokReact>
      );
    }
    return null;
  }

  private handleStoryblokLoad(): void {
    this.loadStory();

    const storyblokInstance = StoryblokService.getObjectV2();

    if (storyblokInstance) {
      storyblokInstance.on(['change', 'published'], this.loadStory);

      storyblokInstance.on(['input'], (data: ISbStory['data']) => {
        const story = data?.story as StoryDataFromGraphQLQuery;

        this.setState({
          story,
          ...DomService.getGlobalConfig({
            pageId: story.uuid,
            locale: StoryblokService.getCountryCode(story).locale,
            country: StoryblokService.getCountryCode(story).country,
          }),
        });
      });
    }
  }

  async loadStory(): Promise<void> {
    const storyblokConfig = StoryblokService.getConfig();
    const timeStamp = new Date().getTime();

    const countryCode = window.location.pathname.split('/')[1];
    const defaultDatasourceEntries: StoryblokDatasourceEntry[] = await this.storyblokClient.getAll('cdn/datasource_entries', {
      cv: timeStamp,
      per_page: 500,
    });
    if (storyblokConfig) {
      this.storyblokClient
        .get(`cdn/stories/${countryCode}/global/fund`, {
          version: 'published',
        })
        .then(async (response) => {
          const { story } = response.data;
          const storyblokDatasourceEntries: StoryblokDatasourceEntry[] = await this.storyblokClient.getAll('cdn/datasource_entries', {
            cv: timeStamp,
            dimension: countryCode,
            per_page: 500,
          });
          const globalContentEntries = StoryblokService.parseDatasourceEntries(
            StoryblokService.getLocalizedDatasourceEntries({
              datasourceEntries: [storyblokDatasourceEntries],
              // for the editor view we load only the datasources for this country
              dimensions: [countryCode],
              countryCode,
              defaultValue: defaultDatasourceEntries,
            }),
          );
          this.setState({
            story,
            ...DomService.getGlobalConfig({ pageId: story.uuid, locale: countryCode.split('-')[1], country: countryCode.split('-')[0] }),
            globalContent: globalContentEntries,
          });
          this.loadNavigation();
        });
    }
  }

  private async loadNavigation(): Promise<void> {
    const countryCode = window.location.pathname.split('/')[1];

    const allStories = await this.storyblokClient.getAll('cdn/stories', {
      excluding_slugs: '*/global/*, */category/*, */article/*',
      starts_with: `${countryCode}/`,
    });
    const tree = await NavigationService.getNavigation(allStories, countryCode.split('-')[1]);

    this.setState({ navigation: tree });
  }
}
